import { clamp, Vec3 } from '../core/math';
import { MASTS, type MastId, halfBreadthXY, sheerY, deckY } from '../data/ship';
import type { ShipClass } from '../data/classes';
import type { SimEvents } from './events';
import type { Ship } from './ship';

// Localised, persistent damage. There is no single health bar: the hull is
// split into sections per side, and shot punches holes that stay where they
// hit, breaches rails, wounds masts and rigging, the rudder, decks, cargo and
// the frames themselves. Every piece feeds back into how the ship sails,
// fights and floats. All positions are in design space (see Ship).

export const SECTION_NAMES = ['Bow', 'Forward', 'Waist', 'Aft', 'Stern'] as const;
/** Design-space x boundaries of the five sections (and hold compartments). */
export const SECTION_BOUNDS = [-18.1, -9, -2.5, 4, 10.5, 18.9] as const;

export function sectionAt(xd: number): number {
  for (let i = 0; i < 5; i++) if (xd < SECTION_BOUNDS[i + 1]!) return i;
  return 4;
}

/** Stern is section 0; bow is 4 (x runs aft to forward). */
export function sectionName(i: number): string {
  return SECTION_NAMES[4 - i]!;
}

export interface Hole {
  x: number;
  y: number;
  z: number;
  /** Physical radius of the opening, m. */
  r: number;
  side: number;
  /** 0 = open .. 1 = plugged by the carpenters. */
  patched: number;
  /** Charred edges (shell / fire). */
  burnt: boolean;
}

export interface Breach {
  x: number;
  /** Half width (design m). */
  w: number;
  /** How far down from the rail the bulwark is gone (design m). */
  depth: number;
  side: number;
}

export interface MastDamage {
  id: MastId;
  hp: number;
  max: number;
  /** Standing rigging supporting it, 0..1. */
  rigging: number;
  /** 0 standing, 1 topgallant gone, 2 topmast gone, 3 lower mast gone. */
  lostLevel: number;
  /** Height (design y) of the stump when the lower mast went. */
  stump: number;
}

export class DamageModel {
  /** Hull integrity per [section][side (0 port, 1 starboard)]. */
  readonly hull: number[][];
  readonly hullMax: number;
  readonly deck: number[];
  /** Keel and frames per section: when the waist goes, she breaks her back. */
  readonly structure: number[];
  readonly cargo: number[];
  readonly masts: Record<string, MastDamage> = {};
  rudder = 1;
  readonly holes: Hole[] = [];
  readonly breaches: Breach[] = [];
  /** Powder magazine: below the waist, aft of the main hatch. */
  readonly magazine = { x: -1.8, y: -1.8, heat: 0, exploded: false };
  brokenBack = false;
  /** Bumped when the visible damage changes. */
  version = 0;
  hits = 0;

  constructor(readonly cls: ShipClass) {
    this.hullMax = 100 * cls.hull;
    this.hull = [0, 1, 2, 3, 4].map(() => [this.hullMax, this.hullMax]);
    this.deck = [0, 1, 2, 3, 4].map(() => 60 * cls.hull);
    this.structure = [0, 1, 2, 3, 4].map(() => 140 * cls.hull);
    this.cargo = [0, 1, 2, 3, 4].map(() => 1);
    for (const m of MASTS) {
      if (!cls.masts.includes(m.id)) continue;
      const max = 70 * Math.pow(m.radius / 0.42, 2) * Math.max(0.6, cls.scale.x);
      this.masts[m.id] = { id: m.id, hp: max, max, rigging: 1, lostLevel: 0, stump: 0 };
    }
  }

  /** Overall hull integrity 0..1 (worst side weighted in). */
  get integrity(): number {
    let sum = 0, worst = 1;
    for (const s of this.hull) {
      for (const hp of s) {
        const f = clamp(hp / this.hullMax, 0, 1);
        sum += f;
        worst = Math.min(worst, f);
      }
    }
    return (sum / 10) * 0.7 + worst * 0.3;
  }

  /** Open area of holes below the design load line, m^2 (for AI and HUD). */
  get leakArea(): number {
    let a = 0;
    for (const h of this.holes) if (h.y < 0.8) a += Math.PI * h.r * h.r * (1 - h.patched);
    return a;
  }

  damageHull(section: number, side: number, amount: number, events: SimEvents | null, where?: { x: number; y: number; z: number }): void {
    const s = side > 0 ? 1 : 0;
    const before = this.hull[section]![s]!;
    this.hull[section]![s] = before - amount;
    // Once the planking is gone the frames take the blows.
    if (this.hull[section]![s]! < 0) {
      this.structure[section]! -= Math.min(amount, -this.hull[section]![s]!) * 0.8;
      this.hull[section]![s] = Math.max(this.hull[section]![s]!, -this.hullMax * 0.5);
    }
    if (before > 0 && this.hull[section]![s]! <= 0 && events && where) {
      events.push({ type: 'collapse', x: where.x, y: where.y, z: where.z, what: 'hull', size: 1 });
    }
    this.hits++;
    this.version++;
  }

  addHole(x: number, y: number, z: number, r: number, burnt = false): Hole {
    // Close hits open up an existing hole instead of making a new one.
    for (const h of this.holes) {
      const d = Math.hypot(h.x - x, h.y - y, h.z - z);
      if (d < h.r * 2 + r) {
        h.r = Math.min(h.r + r * 0.6, 1.4);
        h.patched *= 0.5;
        h.burnt ||= burnt;
        this.version++;
        return h;
      }
    }
    const hole: Hole = { x, y, z, r, side: Math.sign(z) || 1, patched: 0, burnt };
    if (this.holes.length < 60) this.holes.push(hole);
    this.version++;
    return hole;
  }

  breachRail(x: number, side: number, amount: number): void {
    for (const b of this.breaches) {
      if (b.side === side && Math.abs(b.x - x) < b.w + 0.6) {
        b.w = Math.min(b.w + amount * 0.3, 3);
        b.depth = Math.min(b.depth + amount * 0.25, sheerY(x) - deckY(x) + 0.1);
        this.version++;
        return;
      }
    }
    if (this.breaches.length < 24) {
      this.breaches.push({ x, w: 0.35 + amount * 0.3, depth: 0.3 + amount * 0.35, side });
      this.version++;
    }
  }

  /** Wound a mast; returns the level carried away (0 = none). */
  damageMast(id: MastId, amount: number, yd: number, events: SimEvents | null, fallSide: number, where?: { x: number; y: number; z: number }): number {
    const m = this.masts[id];
    if (!m || m.lostLevel >= 3) return 0;
    const spec = MASTS.find((s) => s.id === id)!;
    // Upper masts are slighter; a hit high up takes them off quicker.
    const level = yd > spec.topmastTop - 1 ? 2 : yd > spec.lowerTop - 1 ? 1 : 0;
    const mult = level === 2 ? 3 : level === 1 ? 1.8 : 1;
    m.hp -= amount * mult * (1.3 - m.rigging * 0.3);
    this.hits++;
    this.version++;
    if (m.hp > 0) return 0;
    // Carried away at the hit, or at the weakest point above it.
    const lost = level === 0 ? 3 : level === 1 ? 2 : 1;
    if (lost <= m.lostLevel) return 0;
    m.lostLevel = lost;
    if (lost === 3) m.stump = clamp(yd, spec.deckY + 1.2, spec.lowerTop - 2);
    m.hp = lost === 3 ? 0 : m.max * 0.5;
    if (events && where) events.push({ type: 'mastFall', x: where.x, y: where.y, z: where.z, mast: id, level: lost, side: fallSide });
    return lost;
  }

  damageRigging(id: MastId, amount: number): void {
    const m = this.masts[id];
    if (!m) return;
    m.rigging = clamp(m.rigging - amount, 0, 1);
    this.version++;
  }

  /** Per-step consequences: leaks, masts working loose, frames failing. */
  update(dt: number, ship: Ship, windPressure: number, events: SimEvents): void {
    for (const h of this.holes) {
      const area = Math.PI * h.r * h.r * (1 - h.patched);
      if (h.y < 2.2) ship.leak(h.x, h.y, h.z, area, dt);
    }
    // Shot-torn planking and trailing wreckage drag.
    let holeDrag = 0;
    for (const h of this.holes) if (h.y < 0.5) holeDrag += h.r * (1 - h.patched);
    ship.dragFactor = 1 + holeDrag * 0.35 + this.wreckDrag;
    ship.rudderHealth = clamp(this.rudder, 0, 1);
    // Masts with little rigging left work loose in a blow.
    for (const m of Object.values(this.masts)) {
      if (m.lostLevel >= 3) continue;
      if (m.rigging < 0.5 && windPressure > 60) m.hp -= (0.5 - m.rigging) * (windPressure - 60) * 0.004 * dt;
      if (m.hp <= 0) {
        const spec = MASTS.find((s) => s.id === m.id)!;
        const p = ship.designToWorld(tmp.set(spec.x, spec.lowerTop * 0.6, 0), tmp);
        this.damageMast(m.id, 1, spec.lowerTop * 0.55, events, Math.sign(ship.heel) || 1, p);
      }
    }
    // A shattered waist under the weight of water breaks her back.
    if (!this.brokenBack && this.structure[2]! < 0 && ship.flooding > 0.35) {
      this.brokenBack = true;
      const p = ship.designToWorld(tmp.set(0.5, 1, 0), tmp);
      events.push({ type: 'collapse', x: p.x, y: p.y, z: p.z, what: 'keel', size: 3 });
      for (const c of ship.compartments) c.water = Math.min(c.capacity, c.water + c.capacity * 0.25);
      this.version++;
    }
  }

  /** Extra drag from masts over the side before they are cut away. */
  wreckDrag = 0;

  /** Is design point (x, y, z) inside the hull shell? */
  static inside(x: number, y: number, z: number): boolean {
    const hb = halfBreadthXY(x, y);
    return hb > 0 && Math.abs(z) < hb;
  }
}

const tmp = new Vec3();
